/**
 * Tier-4 creatures (M21, docs/TOWERS_V2.md "Tier 4"): the Phoenix Roost's phoenix and the Venom Nest's spiders. Both are
 * plain records the views read (`LevelSim.phoenixes`, `LevelSim.spiders`), moved on the sim's clock.
 */
import { DamageType } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared } from "../../world/roadGeometry";
import { SimConstants } from "../constants";
import { Enemy, Tower } from "../entities";
import { SimEventKind } from "../events";
import type { LevelSim } from "../levelSim";
import { applyDamage } from "../systems/combat";
import { BASE_TIERS, branchOf, isBranch, skill } from "./ascension";
import { auraSlowOn, stun } from "./statuses";

/**
 * A phoenix: circles its roost at `orbitRadius` once every `orbitTicks`, scorching every enemy within `hitRadius` of it
 * (ground and air) every `hitEveryTicks` for the roost's damage × phoenixDamageMult (fire: Fireproof shrugs it off). It burns out
 * after `lifeTicks` (PhoenixFell) and rises again `respawnTicks` later (PhoenixRisen). Ember Nova bursts around it.
 */
export interface Phoenix {
  readonly towerId: number;
  alive: boolean;
  /** Where it is now (cells), and how far round its orbit (0..orbitTicks). */
  x: Fix64;
  y: Fix64;
  orbit: number;
  lifeLeft: number;
  respawnLeft: number;
  novaLeft: number;
}

/** A Venom Nest spider: at home (`enemyId` 0, waiting `cooldown`), or riding an enemy for `ticksLeft`, biting and slowing it. */
export interface Spider {
  readonly id: number;
  readonly towerId: number;
  enemyId: number;
  ticksLeft: number;
  cooldown: number;
  x: Fix64;
  y: Fix64;
}

/** The unit circle at 16 points (cos, sin of k × 22.5°), six decimals: an orbit with no trigonometry at runtime. */
const CIRCLE: readonly (readonly [Fix64, Fix64])[] = [
  ["1", "0"], ["0.923880", "0.382683"], ["0.707107", "0.707107"], ["0.382683", "0.923880"],
  ["0", "1"], ["-0.382683", "0.923880"], ["-0.707107", "0.707107"], ["-0.923880", "0.382683"],
  ["-1", "0"], ["-0.923880", "-0.382683"], ["-0.707107", "-0.707107"], ["-0.382683", "-0.923880"],
  ["0", "-1"], ["0.382683", "-0.923880"], ["0.707107", "-0.707107"], ["0.923880", "-0.382683"],
].map(([c, s]) => [Fix64.parse(c), Fix64.parse(s)] as const);

/** A point on the orbit `orbit` ticks round of `period`, linearly between the 16 table points. */
export function orbitPoint(cx: Fix64, cy: Fix64, radius: Fix64, orbit: number, period: number): [Fix64, Fix64] {
  const n = CIRCLE.length;
  const pos = Fix64.fromInt(orbit % Math.max(1, period)).mulInt(n).divInt(Math.max(1, period));
  const i = pos.floorToInt(), f = pos.frac();
  const [c0, s0] = CIRCLE[i % n], [c1, s1] = CIRCLE[(i + 1) % n];
  const c = c0.add(c1.sub(c0).mul(f)), s = s0.add(s1.sub(s0).mul(f));
  return [cx.add(c.mul(radius)), cy.add(s.mul(radius))];
}

/** Once per wave tick: phoenixes fly, burn and rise; spiders leap, ride, bite and come home. */
export function tickMinions(sim: LevelSim): void {
  syncPhoenixes(sim);
  for (const p of sim.phoenixes) {
    const t = sim.findTower(p.towerId);
    if (t !== null) tickPhoenix(sim, t, p);
  }
  syncSpiders(sim);
  for (const s of sim.spiders) {
    const t = sim.findTower(s.towerId);
    if (t !== null) tickSpider(sim, t, s);
  }
}

/** One phoenix per tier-4 Phoenix Roost standing (placement order); a gone roost's phoenix goes with it. */
function syncPhoenixes(sim: LevelSim): void {
  for (let i = sim.phoenixes.length - 1; i >= 0; i--) {
    const t = sim.findTower(sim.phoenixes[i].towerId);
    if (t === null || isBranch(sim, t, "phoenix") === null) sim.phoenixes.splice(i, 1);
  }
  for (const t of sim.towerSlots) {
    if (!t.alive || t.tier <= BASE_TIERS || isBranch(sim, t, "phoenix") === null || sim.phoenixes.some((p) => p.towerId === t.id)) continue;
    const b = branchOf(sim, t)!;
    const p: Phoenix = { towerId: t.id, alive: true, x: Fix64.fromInt(t.cell.x), y: Fix64.fromInt(t.cell.y), orbit: 0, lifeLeft: b.tier4.int("lifeTicks"), respawnLeft: 0, novaLeft: 0 };
    sim.phoenixes.push(p);
    sim.events.add(SimEventKind.PhoenixRisen, t.id, 0);
  }
}

function tickPhoenix(sim: LevelSim, t: Tower, p: Phoenix): void {
  const b = branchOf(sim, t)!;
  const cx = Fix64.fromInt(t.cell.x), cy = Fix64.fromInt(t.cell.y);
  const rebirth = skill(sim, t, "rebirth");
  if (!p.alive) {
    if (t.disabledTicksLeft > 0 || --p.respawnLeft > 0) return;
    p.alive = true; p.lifeLeft = b.tier4.int("lifeTicks");
    sim.events.add(SimEventKind.PhoenixRisen, t.id, rebirth !== null ? 1 : 0);
    if (rebirth !== null) {
      sim.events.add(SimEventKind.SkillFired, t.id, rebirth.index, 0);
      burst(sim, t, p.x, p.y, rebirth.params.num("burstRadius"), sim.damageOf(t).mul(rebirth.params.num("burstDamageMult")));
    }
    return;
  }
  p.orbit = (p.orbit + 1) % Math.max(1, b.tier4.int("orbitTicks", 1));
  [p.x, p.y] = orbitPoint(cx, cy, b.tier4.num("orbitRadius"), p.orbit, b.tier4.int("orbitTicks", 1));
  if (t.disabledTicksLeft > 0) return;
  const every = Math.max(1, b.tier4.int("hitEveryTicks", 1));
  if (p.orbit % every === 0) {
    const r = b.tier4.num("hitRadius");
    burst(sim, t, p.x, p.y, r, sim.damageOf(t).mul(b.tier4.num("phoenixDamageMult")));
  }
  const nova = skill(sim, t, "emberNova");
  if (nova !== null && --p.novaLeft <= 0) {
    p.novaLeft = nova.params.int("cooldownTicks");
    sim.events.add(SimEventKind.SkillFired, t.id, nova.index, 0);
    burst(sim, t, p.x, p.y, nova.params.num("radius"), sim.damageOf(t).mul(nova.params.num("damageMult")));
  }
  if (--p.lifeLeft <= 0) {
    p.alive = false;
    p.respawnLeft = rebirth !== null ? rebirth.params.int("respawnTicks") : b.tier4.int("respawnTicks");
    sim.events.add(SimEventKind.PhoenixFell, t.id);
  }
}

/** A blaze around (x, y): every enemy within `radius` takes `damage` as burn (Fireproof shrugs it off). */
function burst(sim: LevelSim, t: Tower, x: Fix64, y: Fix64, radius: Fix64, damage: Fix64): void {
  const r2 = radius.mul(radius);
  sim.areaDepth++;
  for (const e of sim.enemySlots) if (e.alive && sim.targetable(e) && distanceSquared(x, y, e.x, e.y).le(r2)) applyDamage(sim, e, t, damage, DamageType.Burn);
  sim.areaDepth--;
}

/** Each tier-4 Venom Nest keeps `spiders` (+ Brood) spiders; a gone nest's spiders go with it. */
function syncSpiders(sim: LevelSim): void {
  for (let i = sim.spiders.length - 1; i >= 0; i--) {
    const t = sim.findTower(sim.spiders[i].towerId);
    if (t === null || isBranch(sim, t, "venom") === null) sim.spiders.splice(i, 1);
  }
  for (const t of sim.towerSlots) {
    if (!t.alive || t.tier <= BASE_TIERS) continue;
    const b = isBranch(sim, t, "venom");
    if (b === null) continue;
    const want = b.tier4.int("spiders") + (skill(sim, t, "brood")?.params.int("spiders") ?? 0);
    let have = 0;
    for (const s of sim.spiders) if (s.towerId === t.id) have++;
    for (; have < want; have++) sim.spiders.push({ id: sim.nextSpiderId++, towerId: t.id, enemyId: 0, ticksLeft: 0, cooldown: 0, x: Fix64.fromInt(t.cell.x), y: Fix64.fromInt(t.cell.y) });
  }
}

function tickSpider(sim: LevelSim, t: Tower, s: Spider): void {
  const b = branchOf(sim, t)!;
  if (s.enemyId !== 0) {
    const e = sim.findEnemy(s.enemyId);
    if (e === null || e.burrowed || --s.ticksLeft <= 0) { goHome(sim, t, s, b.tier4.int("returnTicks")); return; }   // a Burrower going under shakes it off
    s.x = e.x; s.y = e.y;
    auraSlowOn(sim, e, b.tier4.num("slowFraction"));
    applyDamage(sim, e, t, sim.damageOf(t).mul(b.tier4.num("biteDpsMult")).divInt(SimConstants.TicksPerSecond), DamageType.Magic);
    return;
  }
  s.x = Fix64.fromInt(t.cell.x); s.y = Fix64.fromInt(t.cell.y);
  if (s.cooldown > 0) { s.cooldown--; return; }
  if (t.disabledTicksLeft > 0) return;
  const e = spiderTarget(sim, t);
  if (e === null) return;
  s.enemyId = e.id; s.ticksLeft = b.tier4.int("latchTicks"); s.x = e.x; s.y = e.y;
  sim.events.add(SimEventKind.SpiderLatched, s.id, e.id, t.id);
  const snare = skill(sim, t, "webSnare");
  if (snare !== null) { stun(sim, e, t, snare.params.int("stunTicks")); sim.events.add(SimEventKind.SkillFired, t.id, snare.index, e.id); }
}

function goHome(sim: LevelSim, t: Tower, s: Spider, cooldown: number): void {
  sim.events.add(SimEventKind.SpiderReturned, s.id, s.enemyId, t.id);
  s.enemyId = 0; s.ticksLeft = 0; s.cooldown = cooldown;
  s.x = Fix64.fromInt(t.cell.x); s.y = Fix64.fromInt(t.cell.y);
}

/** The ground enemy in the nest's range nearest the gate that no spider of this nest rides (ties → lower id). */
function spiderTarget(sim: LevelSim, t: Tower): Enemy | null {
  const r = sim.rangeOf(t), r2 = r.mul(r);
  const tx = Fix64.fromInt(t.cell.x), ty = Fix64.fromInt(t.cell.y);
  let best: Enemy | null = null;
  for (const e of sim.enemySlots) {
    if (!e.alive || e.isAir || !sim.targetable(e) || distanceSquared(tx, ty, e.x, e.y).gt(r2)) continue;
    if (sim.spiders.some((s) => s.towerId === t.id && s.enemyId === e.id)) continue;
    if (best === null || e.distance.gt(best.distance) || (e.distance.eq(best.distance) && e.id < best.id)) best = e;
  }
  return best;
}
